import React from "react";

import { formatKstShort } from "../../utils/time";
import { getStrategyMeta } from "../../data/strategyMeta";
import VerdictBadge from "./VerdictBadge";
import CapitalStat from "./CapitalStat";

/**
 * PaperStrategyCard — 전략 한 개의 요약 카드.
 *
 * paper-trader /api/summary 응답의 strategies[i] 한 항목을 받는다.
 * 클릭하면 디테일 페이지로 이동.
 *
 * 상태 시각화 (4단계):
 *   - 진입중 (포지션 보유): brand pulse + 좌측 brand border
 *   - 활성 (24h 이내 거래): gain 점
 *   - 휴면 (24h+ idle): warning 점
 *   - 대기 (거래 0건): muted 점
 *
 * 운영 기간: first_trade_ts ~ 현재 (paper-trader BCR fix 이후).
 *
 * Props.strategy 의 JSON 키는 paper-trader TradeStats 의 lowercase
 * tag 가 정답이지만, 향후 PascalCase 응답 호환을 위해 둘 다 lookup.
 */
const IDLE_WARN_HOURS = 24;

/**
 * "신규" 판정 윈도우(일). go-server 가 전략을 처음 관측한 시각(first_seen_at)이
 * 이 일수 이내면 신규로 본다. paper-trader 에 등록 시각이 없어 first-seen 을
 * 별도 추적한다 (GET /api/paper/first-seen). (신규 배지)
 */
export const NEW_WINDOW_DAYS = 3;

/**
 * first_seen_at(RFC3339 UTC 문자열)이 NEW_WINDOW_DAYS 이내면 true.
 * 값이 없거나 파싱 불가면 false(신규 아님).
 */
export function isRecentlyAdded(firstSeenAt) {
    if (!firstSeenAt) return false;
    const t = Date.parse(firstSeenAt);
    if (Number.isNaN(t)) return false;
    return Date.now() - t < NEW_WINDOW_DAYS * 86400000;
}

/**
 * first-seen 레코드 배열 → 신규(3일 내) 전략 id Set.
 * @param {Array<{strategy_id:string, first_seen_at:string}>} records
 */
export function buildNewIdSet(records) {
    const set = new Set();
    if (!Array.isArray(records)) return set;
    for (const r of records) {
        if (r && r.strategy_id && isRecentlyAdded(r.first_seen_at)) {
            set.add(r.strategy_id);
        }
    }
    return set;
}

/**
 * NewBadge — "신규" 라벨 칩. 디자인 시스템 brand 토큰 사용(채택 ⭐=warning,
 * 상태 dot 과 색이 겹치지 않음). 글리프는 장식이므로 aria-hidden.
 */
export function NewBadge() {
    return (
        <span
            className="inline-flex items-center gap-0.5 text-xs font-semibold text-brand-dark bg-brand-subtle px-1.5 py-0.5 rounded shrink-0 whitespace-nowrap leading-none"
            title={`최근 ${NEW_WINDOW_DAYS}일 내 추가된 전략`}
        >
            <span aria-hidden="true">✦</span>신규
        </span>
    );
}

/**
 * 손실 경고 임계치 (KRW). PnL 이 이 값 이하면 카드 좌측에 loss border 강조.
 * 트랙 A — 손실 누적 카드를 그리드 안에서 즉시 식별하기 위함.
 */
export const LOSS_WARN_KRW = -300_000;

/**
 * paper-trader 응답의 strategies[i] 한 항목을 정렬/필터에 쓸 수 있도록
 * 정규화한다. PaperOverviewPage 의 sort/filter 로직에서 호출.
 */
export function readStrategyFields(s) {
    return {
        id: s?.Strategy ?? s?.strategy ?? "?",
        pnl: s?.PnLKRW ?? s?.pnl_krw ?? 0,
        trades: s?.Trades ?? s?.trades ?? 0,
        wr: s?.WinRate ?? s?.win_rate ?? 0,
        pf: s?.profit_factor ?? s?.ProfitFct ?? s?.profit_fct ?? -1,
        openCount: s?.OpenCount ?? s?.open_count ?? 0,
        category: s?.category ?? "Other",
        lastTradeTs: s?.last_trade_ts ?? s?.LastTradeTs,
        firstTradeTs: s?.first_trade_ts ?? s?.FirstTradeTs,
    };
}

/**
 * 카드 status 를 4-bucket 으로 분류 — 'active'(진입중) / 'live'(24h 거래) /
 * 'idle'(휴면) / 'waiting'(거래 0). 정렬·필터 양쪽에서 사용.
 */
export function classifyStrategy({ openCount, lastTradeTs, trades }) {
    if (openCount > 0) return "active";
    if (!lastTradeTs) {
        return trades === 0 ? "waiting" : "live";
    }
    const idleHours = (Date.now() - new Date(lastTradeTs).getTime()) / 3600 / 1000;
    return idleHours < IDLE_WARN_HOURS ? "live" : "idle";
}

export default function PaperStrategyCard({
    strategy,
    onClick,
    adopted = false,
    family = null,
    showFamily = false,
    verdict = null,
    isNew = false,
}) {
    const f = readStrategyFields(strategy);
    const { id, pnl, trades, wr, pf, openCount, lastTradeTs, firstTradeTs } = f;

    // strategyMeta 에 등록돼 있으면 한글 이름/요약을 카드에 노출 — 암호 ID(h318)
    // 대신 "이 전략이 무엇인지" 를 카드만 봐도 파악할 수 있게 한다.
    const meta = getStrategyMeta(id);
    const displayName = meta?.name || id;
    const summary = meta?.summary;

    const pnlClass =
        pnl > 0 ? "text-gain" : pnl < 0 ? "text-loss" : "text-content-secondary";
    const pfText = pf >= 0 ? pf.toFixed(2) : "—";

    const status = resolveStatus({ openCount, lastTradeTs, trades });
    const hasActive = openCount > 0;
    const lossWarn = !hasActive && pnl <= LOSS_WARN_KRW;
    const uptime = formatUptime(firstTradeTs);

    // border 우선순위: 진입중 > 손실경고 > 기본. 두 색상이 동시에 떠 혼동되지 않도록.
    const borderAccent = hasActive
        ? "border-l-2 border-l-brand"
        : lossWarn
          ? "border-l-2 border-l-loss"
          : "";

    return (
        <button
            type="button"
            onClick={() => onClick && onClick(id)}
            aria-label={`${displayName} 전략 디테일 보기 (상태: ${status.label}${
                isNew ? ", 신규" : ""
            }${lossWarn ? ", 손실 경고" : ""}${adopted ? ", 채택됨" : ""})`}
            className={`text-left bg-surface-card border border-surface-border rounded-lg p-3 hover:bg-surface-hover transition w-full min-h-11 ${borderAccent}`}
        >
            <div className="flex items-start justify-between mb-1.5 gap-2">
                <div className="flex items-start gap-1.5 min-w-0">
                    {adopted && (
                        <span
                            className="text-warning shrink-0 mt-0.5"
                            title="채택됨 (우수 전략 북마크)"
                            aria-hidden="true"
                        >
                            ⭐
                        </span>
                    )}
                    {isNew && <span className="mt-0.5 shrink-0"><NewBadge /></span>}
                    <span className="text-sm font-semibold text-content leading-snug line-clamp-2">
                        {displayName}
                    </span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                    <VerdictBadge strategy={strategy} verdict={verdict} fields={f} />
                    <StatusBadge status={status} />
                </div>
            </div>
            {summary && (
                <p className="text-xs text-content-secondary leading-relaxed line-clamp-2 mb-2">
                    {summary}
                </p>
            )}
            {showFamily && family && (
                <div className="mb-2">
                    <span className="inline-flex items-center gap-1 text-xs text-content-muted bg-surface-elevated px-1.5 py-0.5 rounded">
                        <span aria-hidden="true">{family.glyph}</span>
                        {family.label}
                    </span>
                </div>
            )}
            <CapitalStat pnlKrw={pnl} initialCapital={meta?.initialCapital} />
            <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                    <div className="text-content-muted">PnL (KRW)</div>
                    <div className={`text-sm font-semibold ${pnlClass}`}>
                        {pnl.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </div>
                </div>
                <div>
                    <div className="text-content-muted">거래</div>
                    <div className="text-sm font-semibold text-content">{trades}</div>
                </div>
                <div>
                    <div className="text-content-muted">WR</div>
                    <div className="text-sm font-semibold text-content">
                        {(wr * 100).toFixed(1)}%
                    </div>
                </div>
                <div>
                    <div className="text-content-muted">PF</div>
                    <div className="text-sm font-semibold text-content">{pfText}</div>
                </div>
            </div>
            <div className="mt-2 pt-2 border-t border-surface-border-light grid grid-cols-2 gap-2 text-xs text-content-muted">
                <div className="truncate">
                    운영 <span className="text-content">{uptime}</span>
                </div>
                <div className="truncate text-right">
                    {lastTradeTs ? (
                        <>최근 <span className="text-content">{formatKstShort(lastTradeTs)}</span></>
                    ) : (
                        <span>거래 없음</span>
                    )}
                </div>
            </div>
            {hasActive && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-brand-light font-medium">
                    <span
                        aria-hidden="true"
                        className="inline-block h-1.5 w-1.5 rounded-full bg-brand animate-pulse"
                    />
                    활성 포지션 {openCount}
                </div>
            )}
        </button>
    );
}

function StatusBadge({ status }) {
    const dotClass = {
        brand: "bg-brand animate-pulse",
        gain: "bg-gain",
        warning: "bg-warning",
        muted: "bg-content-muted",
    }[status.tone];
    return (
        <span
            className="inline-flex items-center gap-1 text-xs text-content-secondary shrink-0"
            title={status.title}
        >
            <span
                aria-hidden="true"
                className={`inline-block h-1.5 w-1.5 rounded-full ${dotClass}`}
            />
            {status.label}
        </span>
    );
}

function resolveStatus({ openCount, lastTradeTs, trades }) {
    if (openCount > 0) {
        return { label: "진입중", tone: "brand", title: "활성 포지션 보유" };
    }
    if (!lastTradeTs) {
        if (trades === 0) {
            return {
                label: "신호 대기",
                tone: "muted",
                title: "Paper-trader 가동 중 — 진입 신호 대기 (조건 충족 시 자동 진입). 검증 진행 중.",
            };
        }
        return { label: "활성", tone: "gain", title: "최근 거래 시각 미상" };
    }
    const idleHours = (Date.now() - new Date(lastTradeTs).getTime()) / 3600 / 1000;
    if (idleHours < IDLE_WARN_HOURS) {
        return {
            label: "활성",
            tone: "gain",
            title: `${idleHours.toFixed(1)}시간 전 거래`,
        };
    }
    return {
        label: "휴면",
        tone: "warning",
        title: `${Math.floor(idleHours)}시간 동안 거래 없음`,
    };
}

function formatUptime(firstTs) {
    if (!firstTs) return "—";
    const t = new Date(firstTs).getTime();
    if (isNaN(t)) return "—";
    const ms = Math.max(0, Date.now() - t);
    const days = Math.floor(ms / 86400000);
    const hours = Math.floor((ms % 86400000) / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    if (days > 0) return `${days}일 ${hours}시간`;
    if (hours > 0) return `${hours}시간 ${minutes}분`;
    return `${minutes}분`;
}
